/* eslint-disable complexity */
import deepClone from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';

import * as actions from '~/src/features/tariffBuilder/actions';
import { EAction } from '~/src/features/tariffBuilder/constants/actions';
import { EEntity } from '~/src/features/tariffBuilder/types/EEntity';
import { IDecoderEntity } from '~/src/features/tariffBuilder/types/entity';
import { InferValueTypes } from '~/src/store';

import initialState, { TBaseSubscription, TInitialState, TRouter } from './initialState';

export type TAction = ReturnType<InferValueTypes<typeof actions>>

const reducer = (state: TInitialState = initialState, action: TAction) => {
    switch (action.type) {
        case EAction.Initialization:
            return {
                ...state,
                initialized: false,
                limitations: {
                    [EEntity.routers]: action.payload.routerLimits,
                    [EEntity.decoders]: action.payload.decoderLimits,
                },
            };
        case EAction.Initialized:
            return {
                ...state,
                initialized: true,
                loading: false,

                // @ts-ignore
                ...action.payload,
            };
        case EAction.Loading: {
            return {
                ...state,
                loading: action.payload.state,
            };
        }
        case EAction.SetStep: {
            return {
                ...state,
                currentStep: action.payload.stepId,
                maximumTakenStep: action.payload.stepId > state.maximumTakenStep
                    ? action.payload.stepId
                    : state.maximumTakenStep,
            };
        }
        case EAction.SelectSpeed: {
            return {
                ...state,
                selectedSpeed: action.payload.id,
            };
        }
        case EAction.SetCurrentTariff: {
            return {
                ...state,
                currentTariff: {
                    ...action.payload.state,
                    /**
                     * Сalculate method of the api-tariff answers with a 403 status code, when sending a speed that is a multiple of 50.
                     * To fix this, round the speed to the nearest whole number
                     */
                    speed: action.payload.state?.speed,
                },
            };
        }
        case EAction.SetChangePlanWebcabData: {
            return {
                ...state,
                changePlanWebcabData: action.payload,
            };
        }
        case EAction.SetRequestId: {
            return {
                ...state,
                requestId: action.payload.requestId,
            };
        }
        case EAction.AddEntity: {
            const { entity: entityName, key, period } = action.payload;

            const newEntityState = deepClone<TBaseSubscription | TRouter | IDecoderEntity>(state[entityName][key]);
            newEntityState.quantity = 1;

            if (period) {
                newEntityState.selectedCost = period;
            }

            const selectedEntityType =
                ([EEntity.onlineCinemas, EEntity.antivirus, EEntity.additional, EEntity.security].includes(entityName))
                    ? EEntity.services
                    : entityName as EEntity.decoders | EEntity.routers;

            return {
                ...state,
                [entityName]: {
                    ...state[entityName],
                    [key]: {
                        ...state[entityName][key],
                        ...newEntityState,
                    },
                },
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    [selectedEntityType]: [
                        ...state.selectedEntityIds[selectedEntityType],
                        state[entityName][key].id,
                    ],
                },
            };
        }
        case EAction.AddTvEntity: {
            const { entity: entityName, key, group } = action.payload;

            const newStateForTv = deepClone(state[EEntity.tv]);
            newStateForTv[group].items[key].quantity = 1;

            return {
                ...state,
                [entityName]: newStateForTv,
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    ['services']: [
                        ...state.selectedEntityIds['services'],
                        state[EEntity.tv][group].items[key].id,
                    ],
                },
            };
        }
        case EAction.IncrementEntity: {
            const { entity: entityName, key } = action.payload;

            return {
                ...state,
                [entityName]: {
                    ...state[entityName],
                    [key]: {
                        ...state[entityName][key],
                        quantity: state[entityName][key].quantity + 1,
                    },
                },
            };
        }
        case EAction.DecrementEntity: {
            const { entity: entityName, key } = action.payload;

            const newEntityState = deepClone<TRouter | IDecoderEntity>(state[entityName][key]);
            const selectedEntityType =
                ([EEntity.onlineCinemas, EEntity.antivirus, EEntity.additional, EEntity.security].includes(entityName))
                    ? EEntity.services
                    : entityName;
            let selectedEntities = [...state.selectedEntityIds[selectedEntityType]];
            newEntityState.quantity -= 1;

            if (newEntityState.quantity === 0) {
                if (state.limitations?.[entityName] && !state.limitations[entityName].installment) {
                    newEntityState.selectedCost = Math.max(...Object.keys(newEntityState.costs).map(Number));
                }
                selectedEntities = selectedEntities.filter(eid => eid !== newEntityState.id);
            }

            return {
                ...state,
                [entityName]: {
                    ...state[entityName],
                    [key]: {
                        ...state[entityName][key],
                        ...newEntityState,
                    },
                },
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    [selectedEntityType]: selectedEntities,
                },
            };
        }
        case EAction.RemoveEntity: {
            const { entity: entityName, key } = action.payload;
            const selectedEntityType =
                ([EEntity.onlineCinemas, EEntity.antivirus, EEntity.additional, EEntity.security].includes(entityName))
                    ? EEntity.services
                    : entityName as EEntity.decoders | EEntity.routers;
            const selectedEntities = [...state.selectedEntityIds[selectedEntityType]].filter(eid => eid !== state[entityName][key].id);
            return {
                ...state,
                [entityName]: {
                    ...state[entityName],
                    [key]: {
                        ...state[entityName][key],
                        quantity: 0,
                    },
                },
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    [selectedEntityType]: selectedEntities,
                },
            };
        }
        case EAction.RemoveTvEntity: {
            const { entity: entityName, key, group } = action.payload;

            const selectedEntities = [...state.selectedEntityIds[EEntity.services]].filter(eid => eid !== state[EEntity.tv][group].items[key].id);

            const newStateForTv = deepClone(state[EEntity.tv]);
            newStateForTv[group].items[key].quantity = 0;

            return {
                ...state,
                [entityName]: newStateForTv,
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    [EEntity.services]: selectedEntities,
                },
            };
        }
        case EAction.RemoveAllTvEntities: {
            const newStateForTv = deepClone(state[EEntity.tv]);
            const idsToDelete: number[] = [];
            forEach(newStateForTv, (entity) => {
                forEach(entity.items, (item) => {
                    item.quantity = 0;
                    idsToDelete.push(item.id);
                });
            });
            const selectedEntities = [...state.selectedEntityIds['services']].filter(eid => !idsToDelete.includes(eid));

            return {
                ...state,
                [EEntity.tv]: newStateForTv,
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    ['services']: selectedEntities,
                },
            };
        }
        case EAction.ChangeCost: {
            const { entity: entityName, key, period } = action.payload;

            const newEntityState = deepClone<TRouter | IDecoderEntity>(state[entityName][key]);

            if (period) {
                newEntityState.selectedCost = period;
            }

            return {
                ...state,
                [entityName]: {
                    ...state[entityName],
                    [key]: {
                        ...state[entityName][key],
                        ...newEntityState,
                    },
                },
            };
        }
        case EAction.SetClientName: {
            return {
                ...state,
                client: { ...state.client, name: action.payload.clientName },
            };
        }
        case EAction.SetClientPhone: {
            return {
                ...state,
                client: { ...state.client, phone: action.payload.clientPhone },
            };
        }
        case EAction.SetClientContactPhone: {
            return {
                ...state,
                client: { ...state.client, contactPhone: action.payload.contactPhone },
            };
        }
        case EAction.SetSpeedRestrictions: {
            return {
                ...state,
                selectedSpeed: 0,
                speedRestrictions: action.payload.restrictions,
            };
        }
        case EAction.toggleHasSmartTv: {
            return {
                ...state,
                hasSmartTv: !state.hasSmartTv,
            };
        }
        case EAction.flushDecoders: {
            const decoders = deepClone(state[EEntity.decoders]);

            for (const idx in decoders) {
                const decoder = decoders[idx];
                if (decoder.quantity > 0) {
                    decoder.quantity = 0;
                    decoder.selectedCost = Math.max(...Object.keys(decoder.costs).map(Number));
                }
            }

            return {
                ...state,
                hasSmartTv: false,
                [EEntity.decoders]: decoders,
                selectedEntityIds: {
                    ...state.selectedEntityIds,
                    [EEntity.decoders]: [],
                },
            };
        }
        case EAction.SetSelectedTimeSlot: {
            return {
                ...state,
                selectedTimeSlot: action.payload.timeSlot,
            };
        }
        case EAction.SetAgreementData: {
            return {
                ...state,
                agreement: { ...state.agreement, ...action.payload },
            };
        }
        case EAction.SetReplanIsError: {
            return {
                ...state,
                replanIsError: action.payload.isError,
            };
        }
        case EAction.Finalization:
        default:
            return { ...state };
    }
};

export default reducer;
